import React, { useState, useEffect } from 'react';
import { Heart, Coffee, TreePine, Bug, Crown, Sparkles, Gift, Users, Globe, ArrowRight, Check, Star, Loader2, Trophy, Repeat, Hexagon, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ONE_TIME_TIERS = [
  { id: 'smoker_fuel', name: 'Smoker Fuel', amount: 5, icon: Coffee,
    color: 'from-stone-100 to-stone-50', border: 'border-stone-200', accent: 'text-stone-600',
    description: 'Buy us a coffee (or smoker fuel).', impact: '1 day of server costs' },
  { id: 'hive_tool', name: 'Hive Tool', amount: 15, icon: Bug, popular: true,
    color: 'from-amber-100 to-amber-50', border: 'border-amber-200', accent: 'text-amber-700',
    description: 'Help us build new features.', impact: 'A feature improvement' },
  { id: 'queen_cell', name: 'Queen Cell', amount: 30, icon: Crown,
    color: 'from-purple-100 to-purple-50', border: 'border-purple-200', accent: 'text-purple-700',
    description: 'A royal contribution.', impact: 'A new platform feature' },
  { id: 'full_super', name: 'Full Super', amount: 50, icon: Sparkles,
    color: 'from-green-100 to-green-50', border: 'border-green-200', accent: 'text-green-700',
    description: "You're keeping Thrive alive.", impact: 'Infrastructure for a month' },
];

const MONTHLY_TIERS = [
  { id: 'monthly_5', name: 'Keeper', amount: 5, color: 'border-sky-200 from-sky-50 to-sky-25', accent: 'text-sky-700', description: 'Steady support that adds up. Coffee-a-month for the bees.' },
  { id: 'monthly_10', name: 'Guardian', amount: 10, color: 'border-indigo-200 from-indigo-50 to-indigo-25', accent: 'text-indigo-700', description: 'The backbone of Thrive. Reliable, dependable — like a good queen.', popular: true },
  { id: 'monthly_15', name: 'Champion', amount: 15, color: 'border-amber-200 from-amber-50 to-amber-25', accent: 'text-amber-700', description: 'Above and beyond. You fund an entire feature cycle each year.' },
];

const TIER_ICONS = { 'Smoker Fuel': Coffee, 'Hive Tool': Bug, 'Queen Cell': Crown, 'Full Super': Sparkles, 'Custom': Gift, 'Hive Sponsor': Hexagon, 'Monthly Keeper': Repeat, 'Monthly Guardian': Repeat, 'Monthly Champion': Repeat, 'Custom Monthly': Repeat };

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

const Donations = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('one-time');
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [processing, setProcessing] = useState(null);
  const [donorWall, setDonorWall] = useState({ donors: [], total_raised: 0, total_donors: 0 });
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [sponsorHives, setSponsorHives] = useState([]);
  const [selectedHive, setSelectedHive] = useState(null);
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorMessage, setSponsorMessage] = useState('');

  useEffect(() => {
    axios.get(`${API}/donations/wall`).then(res => setDonorWall(res.data)).catch(() => {});
    axios.get(`${API}/donations/available-hives`).then(res => setSponsorHives(res.data)).catch(() => {});
  }, [paymentSuccess]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const view = searchParams.get('view');
    if (sessionId && view === 'donation-success') {
      setCheckingPayment(true);
      const poll = async (attempts = 0) => {
        if (attempts > 15) { setCheckingPayment(false); return; }
        try {
          const res = await axios.get(`${API}/donations/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            setCheckingPayment(false);
            setPaymentSuccess(true);
            toast.success(`Thank you${res.data.donor_name ? `, ${res.data.donor_name}` : ''}! Your $${res.data.amount} ${res.data.tier_name} donation has been received.`, { duration: 8000 });
            return;
          }
        } catch { /* ignore */ }
        setTimeout(() => poll(attempts + 1), 2000);
      };
      poll();
    }
  }, [searchParams]);

  const handleDonate = async (tier, mode = 'payment') => {
    setProcessing(tier?.id || 'custom');
    try {
      const headers = localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {};
      const res = await axios.post(`${API}/donations/checkout`, {
        tier_id: tier?.id || null,
        custom_amount: tier ? null : parseFloat(customAmount),
        donor_name: donorName,
        origin_url: window.location.origin,
        mode,
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setProcessing(null);
    }
  };

  const handleSponsor = async () => {
    if (!selectedHive || !sponsorName) {
      toast.error('Please select a hive and enter your name');
      return;
    }
    setProcessing('sponsor');
    try {
      const headers = localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {};
      const res = await axios.post(`${API}/donations/sponsor-hive`, {
        hive_id: selectedHive.id,
        donor_name: sponsorName,
        message: sponsorMessage,
        origin_url: window.location.origin,
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start sponsorship checkout');
      setProcessing(null);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl mx-auto" data-testid="donations-page">
      {/* Payment Success Banner */}
      {paymentSuccess && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 text-center" data-testid="donation-success-banner">
          <div className="text-4xl mb-2">🐝</div>
          <h2 className="text-xl font-bold text-green-800">Thank You for Your Donation!</h2>
          <p className="text-sm text-green-700 mt-1">Your generosity helps keep Thrive free for every beekeeper.</p>
        </div>
      )}

      {checkingPayment && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <Loader2 className="h-8 w-8 text-amber-600 animate-spin mx-auto mb-2" />
          <p className="text-amber-800 font-medium">Verifying your donation...</p>
        </div>
      )}

      {/* Hero */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full text-xs font-semibold">
          <Heart className="h-3.5 w-3.5" />
          100% Free Platform — Funded by Beekeepers
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-800 leading-tight">
          Help Keep Thrive <span className="text-amber-600">Free for Every Beekeeper</span>
        </h1>
        <p className="text-stone-500 max-w-xl mx-auto text-sm">
          Bees are under enough pressure. Your donation keeps the platform free, the servers running, and new features coming.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Beekeepers', value: donorWall.total_beekeepers || 0, icon: Users },
          { label: 'Hives Tracked', value: donorWall.total_hives || 0, icon: Globe },
          { label: 'Total Raised', value: `$${donorWall.total_raised.toFixed(0)}`, icon: Heart },
          { label: 'Supporters', value: donorWall.total_donors || 0, icon: Star },
        ].map(s => {
          const I = s.icon;
          return (
            <Card key={s.label} className="bg-amber-50 border-amber-100">
              <CardContent className="p-3 text-center">
                <I className="h-4 w-4 text-amber-600 mx-auto mb-1" />
                <div className="text-lg font-bold text-stone-800">{s.value}</div>
                <div className="text-[10px] text-stone-500">{s.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tab Switch: One-Time / Monthly / Sponsor */}
      <div className="flex justify-center gap-1 bg-stone-100 p-1 rounded-xl max-w-md mx-auto" data-testid="donation-tabs">
        {[
          { id: 'one-time', label: 'One-Time' },
          { id: 'monthly', label: 'Monthly Supporter' },
          { id: 'sponsor', label: 'Sponsor a Hive' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition ${tab === t.id ? 'bg-white shadow text-stone-800' : 'text-stone-500 hover:text-stone-700'}`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── One-Time Tiers ── */}
      {tab === 'one-time' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ONE_TIME_TIERS.map(tier => {
              const TierIcon = tier.icon;
              const busy = processing === tier.id;
              return (
                <Card key={tier.id} className={`relative overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 ${tier.border}`} data-testid={`donation-tier-${tier.id}`}>
                  {tier.popular && <div className="absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg">POPULAR</div>}
                  <CardContent className={`p-5 bg-gradient-to-b ${tier.color}`}>
                    <TierIcon className={`h-7 w-7 ${tier.accent} mb-2`} />
                    <h3 className="font-bold text-stone-800 text-sm">{tier.name}</h3>
                    <div className="text-2xl font-bold text-stone-900 mt-1">${tier.amount}</div>
                    <p className="text-xs text-stone-500 mt-2">{tier.description}</p>
                    <div className="flex items-center gap-1 mt-2 text-[10px] text-stone-400"><Check className="h-3 w-3 text-green-500" />{tier.impact}</div>
                    <Button onClick={() => handleDonate(tier)} disabled={!!processing}
                      className="w-full mt-3 bg-stone-800 hover:bg-stone-900 text-white text-xs h-9" data-testid={`donate-btn-${tier.id}`}>
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Donate ${tier.amount}<ArrowRight className="h-3.5 w-3.5 ml-1" /></>}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {/* Custom one-time */}
          <Card className="border-stone-200">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-bold">$</span>
                  <input type="number" min="1" value={customAmount} onChange={e => setCustomAmount(e.target.value)}
                    placeholder="Custom amount" className="w-full pl-8 pr-4 py-2.5 border border-stone-200 rounded-xl text-base font-semibold focus:ring-2 focus:ring-amber-300 outline-none" data-testid="custom-donation-amount" />
                </div>
                <input type="text" value={donorName} onChange={e => setDonorName(e.target.value)}
                  placeholder="Your name (for Wall of Thanks)" className="flex-1 px-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-300 outline-none" data-testid="donor-name-input" />
                <Button onClick={() => handleDonate(null)} disabled={!customAmount || parseFloat(customAmount) < 1 || !!processing}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-6 h-11 rounded-xl" data-testid="custom-donate-btn">
                  {processing === 'custom' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Heart className="h-4 w-4 mr-1" />Donate</>}
                </Button>
              </div>
              <p className="text-[10px] text-stone-400 mt-1.5">All amounts in AUD. Processed securely via Stripe.</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Monthly Supporter ── */}
      {tab === 'monthly' && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-indigo-700 bg-indigo-50 px-4 py-1.5 rounded-full text-xs font-semibold mb-3">
              <Repeat className="h-3.5 w-3.5" />
              Recurring Monthly Donation
            </div>
            <p className="text-sm text-stone-500 max-w-md mx-auto">
              Set it and forget it. Your monthly contribution provides steady, reliable funding so we can plan features ahead. Cancel anytime.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {MONTHLY_TIERS.map(tier => {
              const busy = processing === tier.id;
              return (
                <Card key={tier.id} className={`relative overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 ${tier.color}`} data-testid={`monthly-tier-${tier.id}`}>
                  {tier.popular && <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg">POPULAR</div>}
                  <CardContent className="p-5">
                    <Repeat className={`h-7 w-7 ${tier.accent} mb-2`} />
                    <h3 className={`font-bold text-sm ${tier.accent}`}>{tier.name}</h3>
                    <div className="text-2xl font-bold text-stone-900 mt-1">${tier.amount}<span className="text-sm font-normal text-stone-400">/mo</span></div>
                    <p className="text-xs text-stone-500 mt-2">{tier.description}</p>
                    <div className="text-[10px] text-stone-400 mt-2">${tier.amount * 12}/year</div>
                    <Button onClick={() => handleDonate(tier, 'subscription')} disabled={!!processing}
                      className={`w-full mt-3 text-white text-xs h-9 ${tier.accent.includes('sky') ? 'bg-sky-700 hover:bg-sky-800' : tier.accent.includes('indigo') ? 'bg-indigo-700 hover:bg-indigo-800' : 'bg-amber-700 hover:bg-amber-800'}`}
                      data-testid={`monthly-btn-${tier.id}`}>
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Subscribe ${tier.amount}/mo<ArrowRight className="h-3.5 w-3.5 ml-1" /></>}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {/* Custom monthly */}
          <Card className="border-stone-200">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="flex-1 relative">
                  <label className="text-xs font-medium text-stone-600 mb-1 block">Custom monthly amount</label>
                  <span className="absolute left-3 bottom-2.5 text-stone-400 font-bold">$</span>
                  <input type="number" min="1" value={customAmount} onChange={e => setCustomAmount(e.target.value)}
                    placeholder="Any amount" className="w-full pl-8 pr-4 py-2.5 border border-stone-200 rounded-xl text-base font-semibold focus:ring-2 focus:ring-indigo-300 outline-none" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium text-stone-600 mb-1 block">Your name</label>
                  <input type="text" value={donorName} onChange={e => setDonorName(e.target.value)}
                    placeholder="For Wall of Thanks" className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-300 outline-none" />
                </div>
                <Button onClick={() => handleDonate(null, 'subscription')} disabled={!customAmount || parseFloat(customAmount) < 1 || !!processing}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 h-11 rounded-xl">
                  <Repeat className="h-4 w-4 mr-1" />Subscribe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Sponsor a Hive ── */}
      {tab === 'sponsor' && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-green-700 bg-green-50 px-4 py-1.5 rounded-full text-xs font-semibold mb-3">
              <Hexagon className="h-3.5 w-3.5" />
              Sponsor a Hive — $25
            </div>
            <p className="text-sm text-stone-500 max-w-md mx-auto">
              Put your name on a real hive. Your sponsorship badge and personal message will be displayed 
              on the hive card for everyone to see. A meaningful way to say "I support the bees."
            </p>
          </div>

          {/* Hive Picker */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {sponsorHives.map(hive => {
              const isSelected = selectedHive?.id === hive.id;
              const isSponsored = !!hive.sponsored_by;
              return (
                <Card key={hive.id}
                  onClick={() => !isSponsored && setSelectedHive(hive)}
                  className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-green-400 shadow-lg' : isSponsored ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md hover:-translate-y-0.5'}`}
                  data-testid={`sponsor-hive-${hive.id}`}>
                  <CardContent className="p-4 text-center">
                    <Hexagon className={`h-8 w-8 mx-auto mb-2 ${isSponsored ? 'text-amber-400' : isSelected ? 'text-green-500' : 'text-stone-300'}`} />
                    <div className="font-bold text-stone-800 text-sm">#{hive.hive_number}</div>
                    {hive.nickname && <div className="text-xs text-stone-500">"{hive.nickname}"</div>}
                    <div className="text-[10px] text-stone-400 mt-1">{hive.bee_variety || hive.yard}</div>
                    {isSponsored ? (
                      <div className="mt-2 text-[10px] text-amber-600 font-semibold bg-amber-50 rounded-full px-2 py-0.5">Sponsored by {hive.sponsored_by}</div>
                    ) : isSelected ? (
                      <div className="mt-2 text-[10px] text-green-600 font-semibold">Selected</div>
                    ) : (
                      <div className="mt-2 text-[10px] text-stone-400">Available</div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Sponsor Form */}
          {selectedHive && (
            <Card className="border-green-200 bg-green-50/30">
              <CardContent className="p-5 space-y-3">
                <h3 className="font-bold text-green-800 flex items-center gap-2">
                  <Hexagon className="h-5 w-5 text-green-600" />
                  Sponsor Hive #{selectedHive.hive_number} {selectedHive.nickname ? `"${selectedHive.nickname}"` : ''}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-stone-600 mb-1 block">Your Name *</label>
                    <input type="text" value={sponsorName} onChange={e => setSponsorName(e.target.value)}
                      placeholder="Displayed on the hive card" className="w-full px-4 py-2.5 border border-green-200 rounded-xl text-sm focus:ring-2 focus:ring-green-300 outline-none bg-white"
                      data-testid="sponsor-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600 mb-1 block">Message (optional)</label>
                    <input type="text" value={sponsorMessage} onChange={e => setSponsorMessage(e.target.value)}
                      placeholder="e.g., For Grandpa's garden bees" className="w-full px-4 py-2.5 border border-green-200 rounded-xl text-sm focus:ring-2 focus:ring-green-300 outline-none bg-white"
                      data-testid="sponsor-message-input" maxLength={200} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-stone-500">One-time <strong className="text-green-700">$25 AUD</strong></div>
                  <Button onClick={handleSponsor} disabled={!sponsorName || !!processing}
                    className="bg-green-700 hover:bg-green-800 text-white px-6" data-testid="sponsor-checkout-btn">
                    {processing === 'sponsor' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Hexagon className="h-4 w-4 mr-1" />Sponsor This Hive</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Donor Wall of Thanks */}
      {donorWall.donors.length > 0 && (
        <div data-testid="donor-wall">
          <h2 className="text-xl font-bold text-stone-800 mb-4 text-center flex items-center justify-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />Wall of Thanks
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {donorWall.donors.map((donor, i) => {
              const DIcon = TIER_ICONS[donor.tier_name] || Gift;
              return (
                <Card key={i} className="bg-gradient-to-b from-amber-50 to-white border-amber-100 hover:shadow-md transition">
                  <CardContent className="p-3 text-center">
                    <DIcon className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                    <div className="font-semibold text-stone-800 text-sm truncate">{donor.donor_name}</div>
                    <div className="text-[10px] text-amber-600 font-medium">{donor.tier_name} · ${donor.amount}</div>
                    <div className="text-[9px] text-stone-400 mt-0.5">{fmtDate(donor.created_at)}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Other Ways */}
      <div>
        <h2 className="text-xl font-bold text-stone-800 mb-4 text-center">Other Ways to Support</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4 text-center">
              <Users className="h-7 w-7 text-blue-600 mx-auto mb-2" />
              <h3 className="font-bold text-stone-800 text-sm">Spread the Word</h3>
              <p className="text-xs text-stone-500 mt-1">Tell a fellow beekeeper about Thrive.</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <Bug className="h-7 w-7 text-green-600 mx-auto mb-2" />
              <h3 className="font-bold text-stone-800 text-sm">Report Bugs</h3>
              <p className="text-xs text-stone-500 mt-1">Your feedback makes Thrive better for everyone.</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4 text-center">
              <TreePine className="h-7 w-7 text-purple-600 mx-auto mb-2" />
              <h3 className="font-bold text-stone-800 text-sm">Plant for Bees</h3>
              <p className="text-xs text-stone-500 mt-1">Plant bee-friendly flowers. Share on Hive Mind.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="text-center text-xs text-stone-400 pb-8">
        <p>All donations go directly to platform costs. Built with love for beekeepers worldwide.</p>
      </div>
    </div>
  );
};

export default Donations;
